import { type QueryObserverResult, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  getAgentAdoption,
  getAgentDashboard,
  getAgentSession,
  getAgentSessionFacets,
  getAgentSessions,
  getDeliveryImpact,
  getModelComparison,
  getPullRequestFileLines,
  getSessionPullRequests,
  getTaskFit,
  getTaskFitTasks,
} from './agent-miner-client';
import type {
  AgentAdoptionParams,
  AgentAdoptionResponse,
  AgentDashboardParams,
  AgentDashboardResponse,
  AgentMinerSessionDetail,
  AgentMinerSessionFacets,
  AgentMinerSessionListParams,
  AgentMinerSessionsResponse,
  DeliveryImpactParams,
  DeliveryImpactResponse,
  ModelComparisonParams,
  ModelComparisonResponse,
  PullRequestFileLinesResponse,
  SessionPullRequestsResponse,
  TaskFitParams,
  TaskFitResponse,
  TaskFitTasksParams,
  TaskFitTasksResponse,
} from './agent-miner-client.type';

/**
 * Hook to fetch agent dashboard data.
 *
 * @param {AgentDashboardParams} params - The query parameters for filtering dashboard data
 * @param {Partial<UseQueryOptions<AgentDashboardResponse>>} options - Optional React Query options
 * @returns {{ data: AgentDashboardResponse | undefined, query: QueryObserverResult<AgentDashboardResponse> }}
 */
const useAgentDashboard = (
  params: AgentDashboardParams,
  options?: Partial<UseQueryOptions<AgentDashboardResponse>>,
): {
  data: AgentDashboardResponse | undefined;
  query: QueryObserverResult<AgentDashboardResponse>;
} => {
  const query = useQuery({
    queryKey: [
      'agent-miner-dashboard',
      params.organization_id,
      params.portfolio_id,
      params.start_date,
      params.end_date,
      params.time_allocation_type,
      params.task_type ?? '',
      params.points ?? '',
    ],
    queryFn: ({ signal }) => getAgentDashboard(params, signal),
    enabled: !!params.organization_id && !!params.portfolio_id && !!params.start_date && !!params.end_date,
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch a paginated list of agent sessions.
 *
 * @param {AgentMinerSessionListParams} params - Filter, sort, and pagination params
 * @param {Partial<UseQueryOptions<PaginatedResponse<AgentMinerSession>>>} options - Optional React Query options
 * @returns {{ data: PaginatedResponse<AgentMinerSession> | undefined, query: QueryObserverResult<PaginatedResponse<AgentMinerSession>> }}
 */
const useAgentSessions = (
  params: AgentMinerSessionListParams,
  options?: Partial<UseQueryOptions<AgentMinerSessionsResponse>>,
): {
  data: AgentMinerSessionsResponse | undefined;
  query: QueryObserverResult<AgentMinerSessionsResponse>;
} => {
  const query = useQuery({
    queryKey: [
      'agent-miner-sessions',
      params.organization_id,
      params.page ?? 1,
      params.page_size ?? 25,
      params.q ?? '',
      params.status ?? '',
      params.source?.join(',') ?? '',
      params.user_id?.join(',') ?? '',
      params.label?.join(',') ?? '',
      params.start_date ?? '',
      params.end_date ?? '',
      params.has_task ?? '',
      params.min_turns ?? '',
      params.model_family?.join(',') ?? '',
      params.ordering ?? '',
    ],
    queryFn: () => getAgentSessions(params),
    enabled: !!params.organization_id,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch a single agent session with its nested turns and events.
 *
 * @param {string | null | undefined} sessionId - The session id (UUID)
 * @param {Partial<UseQueryOptions<AgentMinerSessionDetail>>} options - Optional React Query options
 * @returns {{ data: AgentMinerSessionDetail | undefined, query: QueryObserverResult<AgentMinerSessionDetail> }}
 */
const useAgentSession = (
  sessionId: string | null | undefined,
  options?: Partial<UseQueryOptions<AgentMinerSessionDetail>>,
): {
  data: AgentMinerSessionDetail | undefined;
  query: QueryObserverResult<AgentMinerSessionDetail>;
} => {
  const query = useQuery({
    queryKey: ['agent-miner-session', sessionId],
    queryFn: () => getAgentSession(sessionId as string),
    enabled: !!sessionId,
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch the Related-PRs attribution for a session. Intended to be
 * gated by the caller (only enabled when the session has related PRs), so the
 * request fires lazily when the Pull Requests tab is opened.
 *
 * @param {string | null | undefined} sessionId - The session id (UUID)
 * @param {Partial<UseQueryOptions<SessionPullRequestsResponse>>} options - Optional React Query options
 * @returns {{ data: SessionPullRequestsResponse | undefined, query: QueryObserverResult<SessionPullRequestsResponse> }}
 */
const useSessionPullRequests = (
  sessionId: string | null | undefined,
  options?: Partial<UseQueryOptions<SessionPullRequestsResponse>>,
): {
  data: SessionPullRequestsResponse | undefined;
  query: QueryObserverResult<SessionPullRequestsResponse>;
} => {
  const query = useQuery({
    queryKey: ['agent-miner-session-prs', sessionId],
    queryFn: ({ signal }) => getSessionPullRequests(sessionId as string, signal),
    staleTime: 30_000,
    ...options,
    // Keep the id-guard after ...options so a caller's `enabled` refines it
    // rather than overriding it; the request stays disabled without a session id.
    enabled: !!sessionId && (options?.enabled ?? true),
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch the tagged diff and per-edit survival for one file of a PR the
 * session fed. Keyed by file so each opened tab fires exactly one request and a
 * revisited tab is served from the cache; disabled until every id is known.
 *
 * @param {string | null | undefined} sessionId - The session id (UUID)
 * @param {string | null | undefined} prId - The pull request id (UUID)
 * @param {string | null | undefined} changeRequestFileId - The selected file id (UUID)
 * @param {Partial<UseQueryOptions<PullRequestFileLinesResponse>>} options - Optional React Query options
 * @returns {{ data: PullRequestFileLinesResponse | undefined, query: QueryObserverResult<PullRequestFileLinesResponse> }}
 */
const usePullRequestFileLines = (
  sessionId: string | null | undefined,
  prId: string | null | undefined,
  changeRequestFileId: string | null | undefined,
  options?: Partial<UseQueryOptions<PullRequestFileLinesResponse>>,
): {
  data: PullRequestFileLinesResponse | undefined;
  query: QueryObserverResult<PullRequestFileLinesResponse>;
} => {
  const query = useQuery({
    queryKey: ['agent-miner-pr-file-lines', sessionId, prId, changeRequestFileId],
    queryFn: ({ signal }) =>
      getPullRequestFileLines(sessionId as string, prId as string, changeRequestFileId as string, signal),
    // A merged patch is immutable; keep it fresh for the whole visit. No
    // placeholderData: the file id is in the key, so carrying the previous
    // file's diff forward would show it under the newly selected tab.
    staleTime: 5 * 60_000,
    ...options,
    // Keep the id-guard after ...options so a caller's `enabled` refines it
    // rather than overriding it; the request stays disabled until every id is known.
    enabled: !!sessionId && !!prId && !!changeRequestFileId && (options?.enabled ?? true),
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch the distinct sources and users that actually have sessions in
 * the organization. Used to populate the Source and User filter dropdowns.
 *
 * @param {string | null | undefined} organizationId - Organization id (required to enable the query)
 * @param {Partial<UseQueryOptions<AgentMinerSessionFacets>>} options - Optional React Query options
 * @returns {{ data: AgentMinerSessionFacets | undefined, query: QueryObserverResult<AgentMinerSessionFacets> }}
 */
const useAgentSessionFacets = (
  organizationId: string | null | undefined,
  options?: Partial<UseQueryOptions<AgentMinerSessionFacets>>,
): {
  data: AgentMinerSessionFacets | undefined;
  query: QueryObserverResult<AgentMinerSessionFacets>;
} => {
  const query = useQuery({
    queryKey: ['agent-miner-facets', organizationId],
    queryFn: () => getAgentSessionFacets(organizationId as string),
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
    ...options,
  });
  return { data: query.data, query };
};

/**
 * Hook to fetch per-model-family delivery metrics for the Model Comparison tab.
 *
 * @param {ModelComparisonParams} params - Organization id and date window
 * @param {Partial<UseQueryOptions<ModelComparisonResponse>>} options - Optional React Query options
 * @returns {{ data: ModelComparisonResponse | undefined, query: QueryObserverResult<ModelComparisonResponse> }}
 */
const useModelComparison = (
  params: ModelComparisonParams,
  options?: Partial<UseQueryOptions<ModelComparisonResponse>>,
): {
  data: ModelComparisonResponse | undefined;
  query: QueryObserverResult<ModelComparisonResponse>;
} => {
  const query = useQuery({
    queryKey: [
      'agent-miner-model-comparison',
      params.organization_id,
      params.start_date,
      params.end_date,
      params.time_allocation_type,
    ],
    queryFn: ({ signal }) => getModelComparison(params, signal),
    enabled: !!params.organization_id && !!params.start_date && !!params.end_date,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch AI-assisted vs baseline delivery impact metrics.
 *
 * @param {DeliveryImpactParams} params - Organization and date window
 * @param {Partial<UseQueryOptions<DeliveryImpactResponse>>} options - Optional React Query options
 * @returns {{ data: DeliveryImpactResponse | undefined, query: QueryObserverResult<DeliveryImpactResponse> }}
 */
const useDeliveryImpact = (
  params: DeliveryImpactParams,
  options?: Partial<UseQueryOptions<DeliveryImpactResponse>>,
): {
  data: DeliveryImpactResponse | undefined;
  query: QueryObserverResult<DeliveryImpactResponse>;
} => {
  const query = useQuery({
    queryKey: [
      'agent-miner-delivery-impact',
      params.organization_id,
      params.start_date,
      params.end_date,
      params.time_allocation_type,
    ],
    queryFn: ({ signal }) => getDeliveryImpact(params, signal),
    enabled: !!params.organization_id && !!params.start_date && !!params.end_date,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch the task-type x model-family rework-rate matrix for the Task
 * Fit tab. The `dimension` param joins the query key so switching between the
 * raw-type and Flora-label views refetches independently.
 *
 * @param {TaskFitParams} params - Organization id, date window, and row dimension
 * @param {Partial<UseQueryOptions<TaskFitResponse>>} options - Optional React Query options
 * @returns {{ data: TaskFitResponse | undefined, query: QueryObserverResult<TaskFitResponse> }}
 */
const useTaskFit = (
  params: TaskFitParams,
  options?: Partial<UseQueryOptions<TaskFitResponse>>,
): {
  data: TaskFitResponse | undefined;
  query: QueryObserverResult<TaskFitResponse>;
} => {
  const query = useQuery({
    queryKey: [
      'agent-miner-task-fit',
      params.organization_id,
      params.start_date,
      params.end_date,
      params.time_allocation_type,
      params.dimension,
    ],
    queryFn: ({ signal }) => getTaskFit(params, signal),
    enabled: !!params.organization_id && !!params.start_date && !!params.end_date,
    // No placeholderData: keeping the prior response would expose another
    // scope's (e.g. another org's) matrix while a new query is fetching, since
    // consumers gate on isPending. Let the scope change fall back to loading.
    staleTime: 30_000,
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch the tasks behind one Task Fit matrix cell for the drill-down
 * panel. Disabled until a cell address is present, so the panel can mount
 * closed without firing a request.
 *
 * @param {TaskFitTasksParams} params - Organization, date window, dimension, and the cell address
 * @param {Partial<UseQueryOptions<TaskFitTasksResponse>>} options - Optional React Query options
 * @returns {{ data: TaskFitTasksResponse | undefined, query: QueryObserverResult<TaskFitTasksResponse> }}
 */
const useTaskFitTasks = (
  params: TaskFitTasksParams,
  options?: Partial<UseQueryOptions<TaskFitTasksResponse>>,
): {
  data: TaskFitTasksResponse | undefined;
  query: QueryObserverResult<TaskFitTasksResponse>;
} => {
  const query = useQuery({
    queryKey: [
      'agent-miner-task-fit-tasks',
      params.organization_id,
      params.start_date,
      params.end_date,
      params.dimension,
      params.row_key,
      params.model_family,
    ],
    queryFn: ({ signal }) => getTaskFitTasks(params, signal),
    enabled:
      !!params.organization_id && !!params.start_date && !!params.end_date && !!params.row_key && !!params.model_family,
    // No placeholderData: the cell address is part of the query key, so keeping
    // the prior response would show the previous cell's tasks under the newly
    // selected model family until the refetch resolves.
    staleTime: 30_000,
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Hook to fetch team-based agent adoption metrics for the Adoption tab.
 *
 * The Adoption tab's overview strip and main section call this independently
 * with identical params; the shared query key dedupes them into one request.
 *
 * @param {AgentAdoptionParams} params - Organization, date window, and optional team scope
 * @param {Partial<UseQueryOptions<AgentAdoptionResponse>>} options - Optional React Query options
 * @returns {{ data: AgentAdoptionResponse | undefined, query: QueryObserverResult<AgentAdoptionResponse> }}
 */
const useAgentAdoption = (
  params: AgentAdoptionParams,
  options?: Partial<UseQueryOptions<AgentAdoptionResponse>>,
): {
  data: AgentAdoptionResponse | undefined;
  query: QueryObserverResult<AgentAdoptionResponse>;
} => {
  const query = useQuery({
    queryKey: [
      'agent-miner-adoption',
      params.organization_id,
      params.portfolio_id ?? '',
      params.start_date,
      params.end_date,
      params.time_allocation_type,
      params.team_ids?.join(',') ?? '',
    ],
    queryFn: ({ signal }) => getAgentAdoption(params, signal),
    enabled: !!params.organization_id && !!params.start_date && !!params.end_date,
    // No `placeholderData` carry-forward: any scope change (org, portfolio,
    // date, allocation, team) must return to the loading state rather than
    // render the previous scope's metrics under the newly selected filters.
    // Cached scopes still render instantly, and same-key revalidation keeps
    // data on screen.
    staleTime: 30_000,
    ...options,
  });

  return { data: query.data, query };
};

export {
  useAgentAdoption,
  useAgentDashboard,
  useAgentSession,
  useAgentSessionFacets,
  useAgentSessions,
  useDeliveryImpact,
  useModelComparison,
  usePullRequestFileLines,
  useSessionPullRequests,
  useTaskFit,
  useTaskFitTasks,
};
